import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { CompassMark, Wordmark } from "@/components/CompassMark";
import { NAV } from "@/data/content";
import { Menu, X } from "lucide-react";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ink-base/85 backdrop-blur-md border-b border-ink-line" : "bg-transparent"
      }`}
    >
      <div className="container-wide flex items-center justify-between h-16 md:h-20">
        <Link to="/" aria-label="Quentin Young, home" data-testid="header-home-link" className="flex items-center gap-3 group">
          <CompassMark size={28} className="transition-opacity duration-300 group-hover:opacity-90" alt="" />
          <Wordmark height={20} className="hidden sm:block" />
          <span className="sm:hidden font-serif tracking-[0.18em] uppercase text-paper-primary text-sm">Quentin Young</span>
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-9">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                  isActive ? "text-gold-warm" : "text-paper-secondary hover:text-paper-primary"
                }`
              }
              end
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-nav"
          data-testid="nav-toggle"
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 text-paper-primary border border-ink-line"
          onClick={() => setOpen((s) => !s)}
        >
          {open ? <X size={16} strokeWidth={1.5} /> : <Menu size={16} strokeWidth={1.5} />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        id="mobile-nav"
        className={`lg:hidden border-t border-ink-line bg-ink-base/95 backdrop-blur-md overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav aria-label="Mobile" className="container-wide py-8 grid gap-5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-mobile-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-serif text-2xl tracking-tight ${isActive ? "text-gold-warm" : "text-paper-primary"}`
              }
              end
            >
              {item.label}
              <span className="block mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-paper-tertiary">
                {String(NAV.findIndex((n) => n.to === item.to) + 1).padStart(2, "0")}
              </span>
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default Header;
